import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core';
import styles from './styles';
import PropTypes from 'prop-types';
import { Map, TileLayer } from 'react-leaflet';
import * as L from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import { IconService } from 'services/icon/iconService';
import TemplateStopOverlappingMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopOverlappingMarkerCluster';
import TemplateStopTemplateMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopTemplateMarkerCluster';
import {
  filterTemplatesForDuplicate,
  filterTemplateStopTemplatesForCoords,
  getBoundingBox,
  getBoundingBoxCoords,
  getElementsWithSamePositionTemplateStop,
  getPolyLinesWithSamePosition,
  getNumTemplateStopsOnMap,
  getPolyLinesFromTemplateStopTemplates,
  filterPolyLinesForDuplicate,
} from 'services/util/mapUtils';
import useMapControls from 'hooks/useMapControls';
import {
  MapControls,
  MarkerCreateFuncs,
  OverlappingMarkerCreateFuncs,
} from '../../MapComponents';
import { withTranslation } from 'react-i18next';

const BaseTemplateStopSelectionMap = React.memo((props) => {
  const {
    className,
    oldTemplates,
    optimizedTemplates,
    displayControls,
    greyOutOldTemplates,
  } = props;

  const map = useRef(null);

  const [overlappingElementGroups, setOverlappingElementGroups] = useState([]);
  const [overlappingPolyLineGroups, setOverlappingPolyLineGroups] = useState(
    []
  );

  const [filteredOldTemplates, setFilteredOldTemplates] = useState([]);
  const [filteredOptimizedTemplates, setFilteredOptimizedTemplates] = useState(
    []
  );

  const [filteredPolyLinesOld, setFilteredPolyLinesOld] = useState([]);
  const [filteredPolyLinesOptimized, setFilteredPolyLinesOptimized] = useState(
    []
  );

  const optimizedTemplateStops = useMemo(() => {
    return optimizedTemplates.reduce(
      (acc, template) => acc.concat(template.stops),
      []
    );
  }, [optimizedTemplates]);

  const oldTemplateStops = useMemo(() => {
    return oldTemplates.reduce(
      (acc, template) => acc.concat(template.stops),
      []
    );
  }, [oldTemplates]);

  const optimizedTemplatesWithStopsWithCoords = useMemo(() => {
    return filterTemplateStopTemplatesForCoords(optimizedTemplates);
  }, [optimizedTemplates]);

  const oldTemplatesWithColorUpdated = useMemo(() => {
    return oldTemplates.map((template) => ({
      ...template,
      color: greyOutOldTemplates ? 'grey' : template.color,
    }));
  }, [oldTemplates, greyOutOldTemplates]);

  const oldTemplatesWithStopsWithCoords = useMemo(() => {
    return filterTemplateStopTemplatesForCoords(oldTemplatesWithColorUpdated);
  }, [oldTemplatesWithColorUpdated]);

  const polyLinesOld = useMemo(
    () =>
      getPolyLinesFromTemplateStopTemplates(
        oldTemplatesWithStopsWithCoords
      ).map((polyLine) => ({
        ...polyLine,
        key: polyLine.positions.join(''),
      })),
    [oldTemplatesWithStopsWithCoords]
  );

  const polyLinesOptimized = useMemo(
    () =>
      getPolyLinesFromTemplateStopTemplates(
        optimizedTemplatesWithStopsWithCoords
      ).map((polyLine) => ({
        ...polyLine,
        key: polyLine.positions.join(''),
      })),
    [optimizedTemplatesWithStopsWithCoords]
  );

  const templateStopsOnMap = getNumTemplateStopsOnMap(
    oldTemplatesWithStopsWithCoords,
    optimizedTemplatesWithStopsWithCoords
  );
  const {
    useCluster,
    setUseCluster,
    useDuplicateSpiderfy,
    setUseDuplicateSpiderfy,
    highLiteDuplicates,
    setHighLiteDuplicates,
  } = useMapControls(templateStopsOnMap, false, false);

  useEffect(() => {
    if (useDuplicateSpiderfy) {
      const overlapping = getElementsWithSamePositionTemplateStop(
        oldTemplatesWithStopsWithCoords.concat(
          optimizedTemplatesWithStopsWithCoords
        )
      );
      const keys = overlapping.map((g) => g[0].key);
      setOverlappingElementGroups(overlapping);

      const overlappingPolyLines = getPolyLinesWithSamePosition(
        polyLinesOld.concat(polyLinesOptimized)
      );
      const polyLineKeys = overlappingPolyLines.map((g) => g[0].key);
      setOverlappingPolyLineGroups(overlappingPolyLines);

      setFilteredOptimizedTemplates(
        filterTemplatesForDuplicate(optimizedTemplatesWithStopsWithCoords, keys)
      );
      setFilteredOldTemplates(
        filterTemplatesForDuplicate(oldTemplatesWithStopsWithCoords, keys)
      );
      setFilteredPolyLinesOld(
        filterPolyLinesForDuplicate(polyLinesOld, polyLineKeys)
      );
      setFilteredPolyLinesOptimized(
        filterPolyLinesForDuplicate(polyLinesOptimized, polyLineKeys)
      );
    } else {
      setOverlappingElementGroups([]);
      setFilteredOptimizedTemplates(optimizedTemplatesWithStopsWithCoords);
      setFilteredOldTemplates(oldTemplatesWithStopsWithCoords);
      setFilteredPolyLinesOld(polyLinesOld);
      setFilteredPolyLinesOptimized(polyLinesOptimized);
    }
  }, [
    oldTemplatesWithStopsWithCoords,
    optimizedTemplatesWithStopsWithCoords,
    optimizedTemplateStops,
    useDuplicateSpiderfy,
    polyLinesOld,
    polyLinesOptimized,
  ]);

  function getTemplateStopMarkerAndAddToMcg(
    template,
    templateStop,
    stopNumber,
    mcg,
    t,
    color
  ) {
    MarkerCreateFuncs.getTemplateStopMarkerTemplateStop({
      template,
      templateStop,
      stopNumber,
      mcg,
      t,
      stopType: templateStop.stopType,
      color: color,
    });
  }

  L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

  const fitMapBoundsToFG = useCallback(() => {
    const coordinates = getBoundingBoxCoords(
      polyLinesOld.concat(polyLinesOptimized),
      oldTemplateStops.concat(optimizedTemplateStops)
    );
    if (coordinates.length <= 1) return;
    const boundingBox = getBoundingBox(coordinates);
    map.current.leafletElement.fitBounds(boundingBox, { padding: [30, 30] });
    // eslint-disable-next-line
  }, [oldTemplateStops]);

  useEffect(() => {
    fitMapBoundsToFG();
  }, [fitMapBoundsToFG]);

  return (
    <Fragment>
      <div>
        <Map
          boundsOptions={{ padding: [10, 10] }}
          center={[50, 10.5]}
          className={className}
          gestureHandling
          ref={map}
          zoom={6}
          zoomSnap={0.25}
        >
          <TileLayer
            attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Fragment>
            {filteredOldTemplates.map((template, index) => (
              <TemplateStopTemplateMarkerCluster
                getClusterIcon={(template) =>
                  IconService.getMarkerClusterIcon(template.color, template.id)
                }
                getTemplateStopMarkerAndAddToMcg={
                  getTemplateStopMarkerAndAddToMcg
                }
                key={template.id + '_old'}
                polyline={filteredPolyLinesOld[index]}
                template={template}
                templateStops={template.stops}
                useCluster={useCluster}
              />
            ))}
            {filteredOptimizedTemplates.map((template, index) => (
              <TemplateStopTemplateMarkerCluster
                getClusterIcon={(template) =>
                  IconService.getMarkerClusterIcon(template.color, template.id)
                }
                getTemplateStopMarkerAndAddToMcg={
                  getTemplateStopMarkerAndAddToMcg
                }
                key={template.id + '_optimized'}
                polyline={filteredPolyLinesOptimized[index]}
                template={template}
                templateStops={template.stops}
                useCluster={useCluster}
              />
            ))}
          </Fragment>
          {overlappingElementGroups.map((elementGroup, index) => (
            <TemplateStopOverlappingMarkerCluster
              getClusterIcon={
                OverlappingMarkerCreateFuncs.getForAddTemplateStopToTemplateMap
              }
              getTemplateStopMarkerAndAddToMcg={
                getTemplateStopMarkerAndAddToMcg
              }
              highLiteDuplicates={highLiteDuplicates}
              key={index}
              overlappingElements={elementGroup}
              overlappingPolyLines={overlappingPolyLineGroups[index]}
            />
          ))}
        </Map>
      </div>
      {displayControls && (
        <div>
          <MapControls
            highLiteDuplicates={highLiteDuplicates}
            setHighLiteDuplicates={setHighLiteDuplicates}
            setUseCluster={setUseCluster}
            setUseDuplicateSpiderfy={setUseDuplicateSpiderfy}
            useCluster={useCluster}
            useDuplicateSpiderfy={useDuplicateSpiderfy}
          />
        </div>
      )}
    </Fragment>
  );
});

BaseTemplateStopSelectionMap.displayName = 'BaseTemplateStopSelectionMap';

BaseTemplateStopSelectionMap.defaultProps = {
  displayControls: true,
};

BaseTemplateStopSelectionMap.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  displayControls: PropTypes.bool,
  oldTemplates: PropTypes.array.isRequired,
  optimizedTemplates: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
  greyOutOldTemplates: PropTypes.bool,
};

export default compose(
  withStyles(styles),
  withTranslation()
)(BaseTemplateStopSelectionMap);
